import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { ADMIN_USER_ROLE_LABELS, ADMIN_USER_ROLE_OPTIONS, useUserFilters } from '../../../../../hooks/useUserFilters';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { copyToClipboard, isSupportedISP } from '../../../../../models/UiUtils';
import { useExportCommunicationHistoryMutation } from '../../../../../queries/User/useExportCommunicationHistoryMutation';
import {
	AccountUserViewModel,
	AccountUsersViewModel,
	AdminPermission,
} from '../../../../../viewmodels/AdminViewModels';
import { DeactivateUserModal } from '../../../../../web/components/DeactivateUserModal';
import { DangerMessage } from '../../../../../web/components/errorMessages/DangerMessage';
import { ExportConfirmationModalV2 } from '../../../../../web/components/ExportConfirmation';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../../web/components/Modal';
import { InviteMoreUsersIcon } from '../../../../../web/components/svgs/icons/InviteMoreUsersIcon';
import { RefreshIcon } from '../../../../../web/components/svgs/icons/RefreshIcon';
import { UserFilters } from '../../../../../web/components/UserFilters';
import { brandPrimary, brandPrimaryHover } from '../../../../../web/styles/colors';
import { AccountUserRow } from '../../../../components/accountDetails/AccountUserRow';
import { GrantSlotMachineSpinModal } from '../../../../components/accountDetails/GrantSlotMachineSpinModal';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { ContactImport } from '../../../../components/ContactImport';
import { ImportHistory } from '../../../../components/ImportHistory';
import { EmailChangeModal, EmailChangeType } from '../../../../components/tools/EmailChangeModal';
import { SharedMailboxModal } from '../../../../components/tools/SharedMailboxModal';
import { UserDialogContent } from '../../../../components/user/UserDialogContent';
import { UserErrorsDialog } from '../../../../components/user/UserErrors';
import { styleSheet } from './styles';

interface Props {
	account: Api.IAccount;
	onMakeMainContact?(contactId: string): void;
	scrollToBottomPortalId?: string;
}

function _Users({ account, onMakeMainContact: onMakeMainContactProp, scrollToBottomPortalId: _ }: Props) {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('Users');
	const errorMessages = useErrorMessages();
	const [accountUsers] = React.useState(
		new AccountUsersViewModel(userSession, {
			id: account?.id,
		})
	);
	const [selectedUser, setSelectedUser] = React.useState<AccountUserViewModel | null>(null);
	const [showImport, setShowImport] = React.useState(false);
	const [userForErrorsDialog, setUserForErrorsDialog] = React.useState<AccountUserViewModel | null>(null);
	const [userForImportHistoryDialog, setUserForImportHistoryDialog] = React.useState<AccountUserViewModel | null>(null);
	const [userForInfoDialog, setUserForInfoDialog] = React.useState<AccountUserViewModel | null>(null);
	const [userForEmailChangeModal, setUserForEmailChangeModal] = React.useState<AccountUserViewModel | null>(null);
	const [userForAuthenticatorChangeEmailModal, setUserForAuthenticatorChangeEmailModal] =
		React.useState<AccountUserViewModel | null>(null);
	const [userForSharedMailboxModal, setUserForSharedMailboxModal] = React.useState<AccountUserViewModel | null>(null);
	const [loadingId, setLoadingId] = React.useState('');
	const [showingSlotMachineSpinModal, setShowingSlotMachineSpinModal] = React.useState(false);
	const [showDeactivateConfirmation, setShowDeactivateConfirmation] = React.useState(false);
	const [userForDeactivateConfirmDialog, setUserForDeactivateConfirmDialog] =
		React.useState<AccountUserViewModel | null>(null);
	const [isLoadingAllUsers, setIsLoadingAllUsers] = React.useState(false);
	const [loadingError, setLoadingError] = React.useState<string | null>(null);

	const userFilters = useUserFilters<AccountUserViewModel>({
		roleOptions: ADMIN_USER_ROLE_OPTIONS,
		roleLabels: ADMIN_USER_ROLE_LABELS,
		getUserRole: (user: AccountUserViewModel) => user.role,
		getDisplayName: (user: AccountUserViewModel) => user.name,
	});

	const loadAllUsers = React.useCallback(async () => {
		if (isLoadingAllUsers) return; // Prevent multiple concurrent loads

		setIsLoadingAllUsers(true);
		setLoadingError(null);

		try {
			let previousCount = 0;
			let currentCount = accountUsers.accountUsers.length;
			let retryCount = 0;
			const maxRetries = 3;

			// Always load at least once, then keep loading until no new users are added
			do {
				previousCount = currentCount;

				try {
					await accountUsers.loadUsers();
					retryCount = 0; // Reset retry count on success
				} catch (error) {
					retryCount++;
					logApiError('LoadUsers-Error', error);

					if (retryCount >= maxRetries) {
						throw new Error(`Failed to load users after ${maxRetries} attempts`);
					}

					// Exponential backoff: 200ms, 400ms, 800ms
					const retryDelay = 200 * Math.pow(2, retryCount - 1);
					await new Promise(resolve => setTimeout(resolve, retryDelay));
					continue;
				}

				currentCount = accountUsers.accountUsers.length;
			} while (currentCount > previousCount);
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to load all users';
			setLoadingError(errorMessage);
			errorMessages.pushApiError({ systemMessage: errorMessage });
		} finally {
			setIsLoadingAllUsers(false);
		}
	}, [accountUsers, isLoadingAllUsers, logApiError, errorMessages]);

	React.useEffect(() => {
		loadAllUsers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const exportCommunicationHistoryMutation = useExportCommunicationHistoryMutation();

	const onExportCommunicationHistoryClosed = () => {
		exportCommunicationHistoryMutation.reset();
	};

	const filteredUsers = React.useMemo(() => {
		return userFilters.filterUsers(accountUsers.accountUsers.toArray());
	}, [accountUsers.accountUsers, userFilters]);
	const refreshUsers = () => {
		accountUsers.reset();
		loadAllUsers();
	};
	const importClick = (user: AccountUserViewModel) => () => {
		setSelectedUser(user);
		setShowImport(true);
	};
	const onInviteLinkClicked = () => {
		if (account) {
			const domainQueryString =
				!!account.emailDomain && account.emailDomain !== 'none.com' && account.emailDomain !== '@none.com'
					? '&domain=' + encodeURIComponent(account.emailDomain)
					: '';
			window.open(
				`${import.meta.env.VITE_APP_URL}/#/user/register/?a=` +
					account.id +
					'&invitedBy=' +
					userSession.user.firstName +
					domainQueryString
			);
		}
	};
	const onChangeEmail = (user: AccountUserViewModel) => () => {
		setUserForEmailChangeModal(user);
	};
	const onAuthenticatorChangeEmail = (user: AccountUserViewModel) => () => {
		setUserForAuthenticatorChangeEmailModal(user);
	};
	const onSharedMailbox = (user: AccountUserViewModel) => () => {
		setUserForSharedMailboxModal(user);
	};
	const onCreateWelcomeEmailClick = (user: AccountUserViewModel) => async () => {
		try {
			const link = await user.getWelcomeEmailLink();
			const done = copyToClipboard(link.url);
			if (done) {
				toast.success('Copied account link to clipboard!');
			} else {
				toast.error('Unable to copy to clipboard.');
			}
		} catch (err) {
			logApiError('CreateWelcomeEmail-Error', err);
			errorMessages.pushApiError(err);
		}
	};
	const onGrantSlotMachineSpinClicked = (user: AccountUserViewModel) => {
		setSelectedUser(user);
		setShowingSlotMachineSpinModal(true);
	};
	const onImportHistory = (user: AccountUserViewModel) => () => {
		setUserForImportHistoryDialog(user);
	};
	const onMakeMainContact = (user: AccountUserViewModel) => () => {
		if (onMakeMainContactProp) {
			onMakeMainContactProp(user.id);
		}
	};
	const onUserDeleted = (user: AccountUserViewModel) => () => {
		accountUsers.accountUsers.removeItems([user]);
	};
	const onUserErrorsClick = (user: AccountUserViewModel) => () => {
		setUserForErrorsDialog(user);
	};
	const openUserNameFullscreenModal = (userId: string) => {
		setLoadingId('');
		const path = `${import.meta.env.VITE_APP_URL}/#/people/${userId}`;
		window.open(path, '_blank');
	};
	const userNamesClick = (user: AccountUserViewModel) => async () => {
		setLoadingId(user.id);
		/**
		 * This is a work around to get the users internal contact id
		 * for the levitate main account as the user have a contact id already.
		 * @NOTE ONLY for levitate paid accounts will this be true.
		 */
		if (account.id === import.meta.env.VITE_MAIN_LEV_ACCOUNT_ID) {
			openUserNameFullscreenModal(user.contactId);
			return;
		}
		try {
			const opResult = await user.getProfileInternalContactId(user.id);
			openUserNameFullscreenModal(opResult.value);
		} catch (error) {
			setLoadingId('');
			logApiError('GetInternalId-Error', error);
			errorMessages.pushApiError(error);
		}
	};
	const resendEmailClick = (userId: string) => async () => {
		try {
			await accountUsers.resendWelcome(userId);
			toast.success('Welcome email resent!');
		} catch (error) {
			errorMessages.pushApiError(error);
		}
	};
	const onChangeUserForDeactivateConfirmDialog = (user: AccountUserViewModel) => () => {
		setUserForDeactivateConfirmDialog(user);
		setShowDeactivateConfirmation(true);
	};
	const onDeactivateConfirmationRequestClose = (deactivate?: boolean) => {
		if (deactivate) {
			userForDeactivateConfirmDialog
				.updateUserStatus('deactivate')
				?.then(results => {
					toast.success(`${getDisplayName(results)} set to ${results.activationStatus.toLocaleLowerCase()}.`);
				})
				.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
				});
		}
		setShowDeactivateConfirmation(false);
	};
	const userClick = (user: AccountUserViewModel) => () => {
		user.impersonate({ account, user: user.toJs() });
		setUserForInfoDialog(user);
	};
	const importHistoryModalClose = () => {
		setUserForImportHistoryDialog(null);
	};

	const onExportCommunicationHistory = (user: AccountUserViewModel) => () => {
		if (!exportCommunicationHistoryMutation.isPending) {
			exportCommunicationHistoryMutation.mutate({
				impersonationContext: {
					account,
					user: user?.toJs(),
				},
			});
		}
	};
	const importModalClose = () => {
		setShowImport(false);
	};

	const onRequestGrantSlotMachineSpinModalClose = () => {
		setSelectedUser(null);
		setShowingSlotMachineSpinModal(false);
	};
	const renderImportHistoryDialog = () => {
		if (!userForImportHistoryDialog) {
			return null;
		}
		return (
			<ImportHistory
				userSession={userSession}
				account={account}
				userVM={userForImportHistoryDialog}
				isOpen={!!userForImportHistoryDialog}
				onRequestClose={importHistoryModalClose}
			/>
		);
	};
	const renderEmailChangeModal = () => {
		return (
			<EmailChangeModal
				userSession={userSession}
				userVM={userForEmailChangeModal || userForAuthenticatorChangeEmailModal}
				account={account}
				isOpen={!!userForEmailChangeModal || !!userForAuthenticatorChangeEmailModal}
				onRequestClose={() => {
					setUserForEmailChangeModal(null);
					setUserForAuthenticatorChangeEmailModal(null);
					refreshUsers();
				}}
				type={userForAuthenticatorChangeEmailModal ? EmailChangeType.Authenticator : EmailChangeType.User}
			/>
		);
	};
	const renderSharedMailboxModal = () => {
		return (
			<SharedMailboxModal
				userSession={userSession}
				userVM={userForSharedMailboxModal}
				account={account}
				isOpen={!!userForSharedMailboxModal}
				onRequestClose={() => {
					setUserForSharedMailboxModal(null);
					refreshUsers();
				}}
			/>
		);
	};
	return (
		<div className={`${css(styleSheet.container)} admin-accountDetail-userContainer`}>
			{loadingError && (
				<div className={css(styleSheet.errorContainer)}>
					<DangerMessage>
						{loadingError}
						<button
							onClick={() => {
								setLoadingError(null);
								loadAllUsers();
							}}
							style={{ marginLeft: 10 }}
						>
							Retry
						</button>
					</DangerMessage>
				</div>
			)}
			<UserFilters
				{...userFilters}
				roleOptions={ADMIN_USER_ROLE_OPTIONS}
				roleLabels={ADMIN_USER_ROLE_LABELS}
				userCount={filteredUsers.length}
			/>
			<table className={`admin-accountDetail-employeeList ${css(styleSheet.employeeList)}`}>
				<thead className={css(styleSheet.employeeListTableHead)}>
					<tr style={{ marginBottom: 40 }}>
						<th
							className={css(styleSheet.employeeListTableHeader, styleSheet.employeeListTableHeadShrink)}
							onClick={refreshUsers}
						>
							<RefreshIcon
								fillColor={brandPrimary}
								hoverColor={brandPrimaryHover}
								className={css(styleSheet.refreshIcon)}
							/>
						</th>
						<th className={`${css(styleSheet.employeeListTableHeadName)} `}>
							<ConditionalAccess permission={AdminPermission.InviteUsers}>
								<button onClick={onInviteLinkClicked} className={css(styleSheet.inviteButton)}>
									<InviteMoreUsersIcon />
									<span style={{ marginLeft: 6 }}>Invite More</span>
								</button>
							</ConditionalAccess>
						</th>
						<th className={css(styleSheet.employeeListTableHeader)} />
						<th className={css(styleSheet.employeeListTableHeader)}>Connected</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Email Import</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Signature</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Add-In</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Mobile</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Tags</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Notes</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Facts</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Send</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Test Email Sent</th>
						{account.features.socialMedia?.enabled && (
							<th className={css(styleSheet.employeeListTableHeader)}>Social Connected</th>
						)}
						<ConditionalAccess hideLoading={true} permission={AdminPermission.Deny}>
							<th className={css(styleSheet.additionalInfo)} />
						</ConditionalAccess>
					</tr>
					{account.emailProviderConfigurations.filter(epc => !isSupportedISP(epc.domain)).length > 0 ? (
						<tr>
							<td colSpan={5}>
								<DangerMessage styles={[styleSheet.unsupportedISP]}>
									One or more users below has a customer ISP email domain that we do not support. The customer must send
									through one of our domains like @myagent.email.
								</DangerMessage>
							</td>
						</tr>
					) : null}
				</thead>
				<tbody>
					{filteredUsers.map(user => {
						return (
							<AccountUserRow
								account={account}
								importClick={importClick(user)}
								isDialogShowing={
									!!userForErrorsDialog ||
									!!userForImportHistoryDialog ||
									!!userForInfoDialog ||
									!!userForEmailChangeModal ||
									!!userForAuthenticatorChangeEmailModal
								}
								isLoadingUser={user.id && loadingId === user.id}
								key={user.id}
								onChangeEmail={onChangeEmail(user)}
								onAuthenticatorChangeEmail={onAuthenticatorChangeEmail(user)}
								onSharedMailbox={onSharedMailbox(user)}
								onCreateWelcomeEmailClick={onCreateWelcomeEmailClick(user)}
								onExportCommunicationHistory={onExportCommunicationHistory(user)}
								onGrantSlotMachineSpinClicked={onGrantSlotMachineSpinClicked}
								onImportHistory={onImportHistory(user)}
								onMakeMainContact={onMakeMainContact(user)}
								onUserDeleted={onUserDeleted(user)}
								onUserErrorsClick={onUserErrorsClick(user)}
								onUserNameClick={userNamesClick(user)}
								resendEmailClick={resendEmailClick(user.id)}
								setUserForDeactivateConfirmDialog={onChangeUserForDeactivateConfirmDialog(user)}
								user={user}
								userClick={userClick(user)}
							/>
						);
					})}
					{!accountUsers.isLoading && <tr className={css(styleSheet.expander)} />}
				</tbody>
			</table>
			{showDeactivateConfirmation && (
				<DeactivateUserModal
					account={account}
					onClose={onDeactivateConfirmationRequestClose}
					userId={userForDeactivateConfirmDialog.id}
				/>
			)}
			{(!!accountUsers.isLoading || isLoadingAllUsers) && (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
					{isLoadingAllUsers && <div className={css(styleSheet.loadingMessage)}>Loading all users...</div>}
				</div>
			)}
			{renderImportHistoryDialog()}
			<ContactImport isOpen={showImport} user={selectedUser} onRequestClose={importModalClose} />
			{userForInfoDialog ? (
				<Modal isOpen={true} onRequestClose={() => setUserForInfoDialog(null)} useDefaultHeader={true}>
					<UserDialogContent user={userForInfoDialog} account={account} />
				</Modal>
			) : null}
			<UserErrorsDialog onClose={() => setUserForErrorsDialog(null)} user={userForErrorsDialog} />
			{renderEmailChangeModal()}
			{userForSharedMailboxModal !== null ? renderSharedMailboxModal() : null}
			<GrantSlotMachineSpinModal
				modalProps={{
					isOpen: showingSlotMachineSpinModal,
					onRequestClose: onRequestGrantSlotMachineSpinModalClose,
				}}
				user={selectedUser}
			/>
			<ExportConfirmationModalV2
				mutationResult={exportCommunicationHistoryMutation}
				modalProps={{
					isOpen: !exportCommunicationHistoryMutation.isIdle,
					onRequestClose: onExportCommunicationHistoryClosed,
				}}
				subTitle={
					<>
						<p>Export of recent history will be delivered to your mailbox</p>
					</>
				}
			/>
		</div>
	);
}

export const Users = observer(_Users);
